import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { OpenReservation } from './actions/open-reservation.action';
import { ResetReservation } from './actions/reset-reservation.action';
import { UpdateMovieTitle } from './actions/update-movie-title.action';
import { UpdateOrderUserData } from './actions/update-order-user-data.action';
import { UpdatePaymentId } from './actions/update-payment-id.action';
import { UpdateScreenRoomNumber } from './actions/update-screen-room-number.action';
import { UpdateScreeningTime } from './actions/update-screening-time.action';
import { UpdateSession } from './actions/update-session.action';
import { UpdateTickets } from './actions/update-tickets.action';
import { UpdateTimer } from './actions/update-timer.action';
import { UpdateVoucher } from './actions/update-vouchers.action';
import { Reservation } from './models/reservation.model';
import { IncreaseFoodQuantity } from './actions/increase-food-quantity.action';
import { DecreaseFoodQuantity } from './actions/decrease-food-quantity.action';
import { RemoveFoodFromOrder } from './actions/remove-food-from-order.action';

export const REMAINING_TIME: string = '25:00';

const defaultState = {
  endSession: false,
  order: null,
  remainingTime: REMAINING_TIME,
  started: false,
  tickets: [],
  foods: [],
  vouchers: null,
  screeningTimeFrom: null,
  movieTitle: null,
  movieOriginalTitle: null,
  screenRoomNumber: null,
  orderUserData: null,
  paymentId: null,
};

@State<Reservation>({
  name: 'reservation',
  defaults: defaultState,
})
@Injectable()
export class ReservationState {
  @Action(OpenReservation)
  start(ctx: StateContext<Reservation>, { order }: Reservation) {
    const state = ctx.getState();
    ctx.patchState({ ...state, started: true, order });
  }

  @Action(UpdateSession)
  updateSession(ctx: StateContext<Reservation>, { closed }: UpdateSession) {
    const state = ctx.getState();
    ctx.setState({ ...state, endSession: closed });
  }

  @Action(UpdateTimer)
  updateTimer(ctx: StateContext<Reservation>, { countdownTime }: UpdateTimer) {
    const state = ctx.getState();
    ctx.patchState({ ...state, remainingTime: countdownTime });
  }

  @Action(ResetReservation)
  resetReservation(ctx: StateContext<Reservation>) {
    const state = ctx.getState();
    ctx.setState({ ...state, ...defaultState });
  }

  @Action(UpdateTickets)
  updateTickets(ctx: StateContext<Reservation>, { tickets }: UpdateTickets) {
    const state = ctx.getState();
    ctx.setState({ ...state, tickets });
  }

  @Action(UpdateVoucher)
  updateVouchers(ctx: StateContext<Reservation>, { vouchers }: UpdateVoucher) {
    const state = ctx.getState();
    ctx.setState({ ...state, vouchers });
  }

  @Action(UpdateMovieTitle)
  updateMovieTitle(ctx: StateContext<Reservation>, { movieTitle, movieOriginalTitle }: UpdateMovieTitle) {
    const state = ctx.getState();
    ctx.setState({ ...state, movieTitle, movieOriginalTitle });
  }

  @Action(UpdateScreeningTime)
  updateScreeningTime(ctx: StateContext<Reservation>, { screeningTimeFrom }: UpdateScreeningTime) {
    const state = ctx.getState();
    ctx.setState({ ...state, screeningTimeFrom });
  }

  @Action(UpdateScreenRoomNumber)
  updateScreenRoomNumber(ctx: StateContext<Reservation>, { screenRoomNumber }: UpdateScreenRoomNumber) {
    const state = ctx.getState();
    ctx.setState({ ...state, screenRoomNumber });
  }

  @Action(UpdateOrderUserData)
  updateOrderUserData(ctx: StateContext<Reservation>, { orderUserData }: UpdateOrderUserData) {
    const state = ctx.getState();
    ctx.setState({ ...state, orderUserData });
  }

  @Action(UpdatePaymentId)
  updatePaymentId(ctx: StateContext<Reservation>, { paymentId }: UpdatePaymentId) {
    const state = ctx.getState();
    ctx.setState({ ...state, paymentId });
  }

  @Action(IncreaseFoodQuantity)
  increaseFoodQuantity(ctx: StateContext<Reservation>, { food }: IncreaseFoodQuantity) {
    if (!food) return;

    const state = ctx.getState();
    const existingFood = state.foods?.find((f) => f.id === food.id);

    let updatedFoods;

    if (existingFood) {
      updatedFoods = state.foods?.map((f) => (f.id === food.id ? { ...f, quantity: (f.quantity || 0) + 1 } : f));
    } else {
      updatedFoods = [...(state.foods || []), { ...food, quantity: 1 }];
    }

    ctx.setState({ ...state, foods: updatedFoods });
  }

  @Action(DecreaseFoodQuantity)
  decreaseFoodQuantity(ctx: StateContext<Reservation>, { food }: DecreaseFoodQuantity) {
    if (!food) return;
    const state = ctx.getState();
    const existingFood = state.foods?.find((f) => f.id === food.id);

    if (!existingFood) return;

    let updatedFoods;

    if (existingFood.quantity > 1) {
      updatedFoods = state.foods?.map((f) => (f.id === food.id ? { ...f, quantity: f.quantity - 1 } : f));
    } else {
      updatedFoods = state.foods?.filter((f) => f.id !== food.id);
    }

    ctx.setState({ ...state, foods: updatedFoods });
  }

  @Action(RemoveFoodFromOrder)
  removeFoodFromOrder(ctx: StateContext<Reservation>, { id }: RemoveFoodFromOrder) {
    const state = ctx.getState();
    ctx.setState({ ...state, foods: state.foods?.filter((f) => f.id !== id) });
  }

  @Selector()
  static getFoodQuantity(state: Reservation) {
    return (foodId: number) => {
      const food = state.foods?.find((food) => food.id === foodId);
      return food?.quantity ?? 0;
    };
  }

  @Selector()
  static getOrderFoods(state: Reservation) {
    return state.foods || [];
  }

  @Selector()
  static getOrderFoodsAmount(state: Reservation): number {
    return state.foods?.reduce((acc, food) => acc + (food.quantity || 0), 0) || 0;
  }

  @Selector()
  static getOrderFoodsTotalPrice(state: Reservation): number {
    return state.foods?.reduce((acc, food) => acc + (food.quantity || 0) * (food.priceBrutto || 0), 0) || 0;
  }

  @Selector()
  static getOrderTotalPrice(state: Reservation): number {
    const foodTotalPrice =
      state.foods?.reduce((acc, food) => acc + (food.quantity || 0) * (food.priceBrutto || 0), 0) || 0;
    const ticketsTotalPrice = state.order?.screeningItems.reduce((acc, ticket) => acc + (ticket.price || 0), 0) || 0;
    return foodTotalPrice + ticketsTotalPrice;
  }

  @Selector()
  static isReservationStarted(state: Reservation) {
    return state.started;
  }

  @Selector()
  static getReservationScreeningId(state: Reservation) {
    return state.order?.screeningId;
  }

  @Selector()
  static getRemainingTime(state: Reservation) {
    return state.remainingTime;
  }

  @Selector()
  static getReservationOrder(state: Reservation) {
    return state.order;
  }

  @Selector()
  static getTickets(state: Reservation) {
    return state.tickets || [];
  }

  @Selector()
  static getVouchers(state: Reservation) {
    return state.vouchers;
  }

  @Selector()
  static getScreeningItems(state: Reservation) {
    return state.order?.screeningItems;
  }

  @Selector()
  static getScreeningTime(state: Reservation) {
    return state.screeningTimeFrom;
  }

  @Selector()
  static getMovieTitle(state: Reservation) {
    return {
      movieTitle: state.movieTitle,
      movieOriginalTitle: state.movieOriginalTitle,
    };
  }

  @Selector()
  static getScreenRoomNumber(state: Reservation) {
    return state.screenRoomNumber;
  }

  @Selector()
  static getOrderUserData(state: Reservation) {
    return state.orderUserData;
  }

  @Selector()
  static getPaymentId(state: Reservation) {
    return state.paymentId;
  }

  @Selector()
  static getDateTimeToLive(state: Reservation) {
    return state.order?.dateTimeToLive;
  }
}
